// src/pages/users/UserForm.js

import React, { useState, useEffect } from 'react';
import useApi from '../../hooks/useApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';

const UserForm = ({ user, onClose, onSaveSuccess }) => {
  const api = useApi();
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    role: '',
    permissions: [],
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const fetchedRoles = await api.get('roles');
        setRoles(fetchedRoles.data || fetchedRoles);
        const fetchedPermissions = await api.get('permissions');
        setAllPermissions(fetchedPermissions.data || fetchedPermissions);
      } catch (err) {
        console.error("Failed to fetch roles/permissions for form:", err);
        setError("Failed to load roles/permissions for the form. " + (err.message || ""));
      }
    };
    fetchDropdownData();

    if (user) {
      setIsEditing(true);
      setFormData({
        name: user.name || '',
        username: user.username || '',
        email: user.email || '',
        password: '',
        role: user.userRole || '',
        permissions: user.permissions || [],
      });
    }
  }, [user, api]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePermissionChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => {
      const currentPermissions = new Set(prev.permissions);
      if (checked) {
        currentPermissions.add(value);
      } else {
        currentPermissions.delete(value);
      }
      return { ...prev, permissions: Array.from(currentPermissions) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEditing) {
        const payload = { ...formData };
        if (!payload.password) {
          delete payload.password;
        }
        await api.put(`users/${user._id}`, payload);
        alert("User updated successfully!");
      } else {
        if (!formData.password) {
          throw new Error("Password is required for new users.");
        }
        await api.post('users', formData);
        alert("User created successfully!");
      }
      onSaveSuccess();
      onClose();
    } catch (err) {
      setError(err.message || "Operation failed.");
      console.error("User form submission error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {loading && <LoadingSpinner />}
      {error && <ErrorMessage message={error} />}

    <div>
      <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name</label>
      <input
        type="text"
        name="name"
        id="name"
        value={formData.name}
        onChange={handleChange}
        required
        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
      />
    </div>


      <div>
        <label htmlFor="username" className="block text-sm font-medium text-gray-700">Username</label>
        <input
          type="text"
          name="username"
          id="username"
          value={formData.username}
          onChange={handleChange}
          required
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
        />
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
        <input
          type="email"
          name="email"
          id="email"
          value={formData.email}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
        />
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
          Password {isEditing ? "(Leave blank to keep current)" : "*"}
        </label>
        <input
          type="password"
          name="password"
          id="password"
          value={formData.password}
          onChange={handleChange}
          required={!isEditing}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
        />
      </div>

      <div>
        <label htmlFor="role" className="block text-sm font-medium text-gray-700">Role</label>
        <select
          name="role"
          id="role"
          value={formData.role}
          onChange={handleChange}
          required
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
        >
          <option value="">Select a role</option>
          {roles.map((role) => (
            <option key={role._id} value={role._id}>{role.name}</option>
          ))}
        </select>
      </div>

      <div className="border border-gray-200 rounded-md p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"> {/* Responsive grid */}
          {allPermissions.map((permission) => (
            <div key={permission._id} className="flex items-center">
              <input
                type="checkbox"
                id={`perm-${permission._id}`}
                name="permissions"
                value={permission._id}
                checked={formData.permissions.includes(permission._id)}
                onChange={handlePermissionChange}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
              />
              <label htmlFor={`perm-${permission._id}`} className="ml-2 text-sm text-gray-900">
                {permission.name}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end space-x-3 mt-6">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {loading ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add User')}
        </button>
      </div>
    </form>
  );
};

export default UserForm;
